import { z } from "zod";

/**
 * An http(s) URL. zod's `url()` accepts ANY scheme, and these values render as
 * links (public passport, panel) — a `javascript:` URL would be stored XSS.
 */
function httpUrl(max: number) {
  return z
    .string()
    .trim()
    .url()
    .max(max)
    .refine((u) => /^https?:\/\//i.test(u), "Only http(s) URLs are allowed");
}
import { billingPlanSchema } from "./billing.js";
import { ORG_ROLES } from "./rbac.js";

/**
 * Shared validation schemas. Used by the API (request validation) and the web
 * app (form validation) so the contract is enforced identically on both sides.
 */

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(254)
  .email("A valid email address is required");

/**
 * Strong password policy. `minLength` is injected from config so deployments
 * can tighten it without code changes. Enforces character-class diversity.
 */
export function passwordSchema(minLength = 12) {
  return z
    .string()
    .min(minLength, `Password must be at least ${minLength} characters`)
    .max(200, "Password must be at most 200 characters")
    .refine((v) => /[a-z]/.test(v), "Must contain a lowercase letter")
    .refine((v) => /[A-Z]/.test(v), "Must contain an uppercase letter")
    .refine((v) => /[0-9]/.test(v), "Must contain a digit")
    .refine((v) => /[^A-Za-z0-9]/.test(v), "Must contain a symbol");
}

/** A 6-digit TOTP code. */
export const totpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, "Enter the 6-digit code from your authenticator app");

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required"),
  /** Present only on the second step of an MFA login. */
  mfaCode: totpCodeSchema.optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = (minLength = 12) =>
  z.object({
    email: emailSchema,
    password: passwordSchema(minLength),
    displayName: z.string().trim().min(1).max(120).optional(),
  });
export type RegisterInput = z.infer<ReturnType<typeof registerSchema>>;

export const changePasswordSchema = (minLength = 12) =>
  z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema(minLength),
  });

/** Request a password-reset link (`POST /auth/password/forgot`). */
export const forgotPasswordSchema = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/** Complete a password reset with the emailed token (`POST /auth/password/reset`). */
export const resetPasswordSchema = (minLength = 12) =>
  z.object({
    token: z.string().min(1).max(512),
    newPassword: passwordSchema(minLength),
  });
export type ResetPasswordInput = z.infer<ReturnType<typeof resetPasswordSchema>>;

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type Pagination = z.infer<typeof paginationSchema>;

export const idParamSchema = z.object({ id: z.string().uuid() });

/* --- Organizations (multi-tenancy) ------------------------------------------ */

/** UUID-shaped values resolve as ids in `:orgId` params, so such a slug would be unreachable. */
const UUID_SHAPED = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** URL-safe organization slug (lowercase letters, digits, inner hyphens). */
export const orgSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "Slug may contain lowercase letters, digits and hyphens")
  .refine((v) => !UUID_SHAPED.test(v), "Slug must not be UUID-shaped (UUIDs are reserved for org ids)");

/**
 * Org-level role (tenant-scoped RBAC). "admin" manages membership + all org
 * content; "editor" writes content; "viewer" reads. The permission set per role
 * lives in `@app/core` rbac.ts (ORG_ROLE_PERMISSIONS).
 */
export const orgMemberRoleSchema = z.enum(ORG_ROLES);
export type OrgMemberRole = z.infer<typeof orgMemberRoleSchema>;

/** Shared org profile fields (name, slug, GS1 prefix) — reused by both create paths. */
const orgProfileFields = {
  name: z.string().trim().min(1).max(120),
  slug: orgSlugSchema,
  /** GS1 Company Prefix as assigned by GS1 (4–12 digits). */
  gs1CompanyPrefix: z
    .string()
    .trim()
    .regex(/^\d{4,12}$/, "GS1 company prefix must be 4–12 digits")
    .optional(),
};

/** Platform-admin provisioning (`POST /admin/orgs`): may name an initial owner. */
export const createOrgSchema = z.object({
  ...orgProfileFields,
  billingPlan: billingPlanSchema.optional(),
  /** Optional initial owner — the creator is NOT auto-enrolled as a member. */
  ownerUserId: z.string().uuid().optional(),
});
export type CreateOrgRequest = z.input<typeof createOrgSchema>;
export type CreateOrgInput = z.infer<typeof createOrgSchema>;

/**
 * Self-service org creation (`POST /orgs`): any authenticated user; the creator
 * becomes the org's first admin. No `plan`/`ownerUserId` — those are platform
 * concerns set out of band.
 */
export const createOrgSelfSchema = z.object(orgProfileFields);
export type CreateOrgSelfRequest = z.input<typeof createOrgSelfSchema>;
export type CreateOrgSelfInput = z.infer<typeof createOrgSelfSchema>;

export const addOrgMemberSchema = z.object({
  userId: z.string().uuid(),
  role: orgMemberRoleSchema,
});
export type AddOrgMemberInput = z.infer<typeof addOrgMemberSchema>;

/** Change an existing member's org role (`PATCH /orgs/:orgId/members/:userId`). */
export const changeOrgMemberRoleSchema = z.object({ role: orgMemberRoleSchema });
export type ChangeOrgMemberRoleInput = z.infer<typeof changeOrgMemberRoleSchema>;

/** Invite someone to an org by email at a chosen org role (`POST /orgs/:orgId/invites`). */
export const createOrgInviteSchema = z.object({
  email: emailSchema,
  role: orgMemberRoleSchema,
});
export type CreateOrgInviteRequest = z.input<typeof createOrgInviteSchema>;
export type CreateOrgInviteInput = z.infer<typeof createOrgInviteSchema>;

/** Accept an org invite by its raw token (`POST /orgs/invites/accept`). */
export const acceptOrgInviteSchema = z.object({ token: z.string().min(1).max(512) });
export type AcceptOrgInviteInput = z.infer<typeof acceptOrgInviteSchema>;

/** `:orgId` route param — accepts either the org UUID or its slug. */
export const orgIdParamSchema = z.object({ orgId: z.string().trim().min(1).max(254) });

/* --- Products & identifiers (DPP domain) ------------------------------------ */

/** GS1 Global Trade Item Number — GTIN-8/12/13/14. */
export const gtinSchema = z
  .string()
  .trim()
  .regex(/^(\d{8}|\d{12,14})$/, "GTIN must be 8, 12, 13 or 14 digits");

/**
 * EU customs commodity code: 8 digits (CN, Combined Nomenclature) up to
 * 10 digits (TARIC). The EU DPP Registry matches the registered UID against
 * the commodity code declared at import (ESPR Art. 13).
 */
export const commodityCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{8,10}$/, "Commodity code must be 8-10 digits (CN/TARIC)");

/**
 * Turkish customs tariff position (GTİP, Gümrük Tarife İstatistik Pozisyonu):
 * 12 digits whose first 8 are the CN code. Turkish exporters declare it on the
 * export declaration; the draft Turkish ESPR regulation (Art. 17) matches the
 * registry's unique registration identifier against the declared commodity
 * code, so both codes live on the product side by side.
 */
export const gtipSchema = z
  .string()
  .trim()
  .regex(/^\d{12}$/, "GTİP must be 12 digits");

export const productStatusSchema = z.enum(["draft", "active", "archived"]);
export type ProductStatus = z.infer<typeof productStatusSchema>;

/** Flat attribute bag keyed by schema field dot-path ids (e.g. "identity.brand"). */
export const productAttrsSchema = z.record(z.string().min(1), z.unknown());

export const createProductSchema = z.object({
  sku: z.string().trim().min(1).max(64),
  name: z.string().trim().min(1).max(200),
  gtin: gtinSchema.optional(),
  commodityCode: commodityCodeSchema.optional(),
  gtip: gtipSchema.optional(),
  /** Sector template id (e.g. "textile-v1") — validated against the schema registry. */
  schemaId: z.string().trim().min(1).max(64),
  attrs: productAttrsSchema.optional(),
});
export type CreateProductInput = z.infer<typeof createProductSchema>;

export const updateProductSchema = z
  .object({
    sku: z.string().trim().min(1).max(64).optional(),
    name: z.string().trim().min(1).max(200).optional(),
    /** null clears the GTIN. */
    gtin: gtinSchema.nullable().optional(),
    /** null clears the commodity code. */
    commodityCode: commodityCodeSchema.nullable().optional(),
    /** null clears the GTİP. */
    gtip: gtipSchema.nullable().optional(),
    schemaId: z.string().trim().min(1).max(64).optional(),
    /** Replaces the whole attribute bag (draft-validated). */
    attrs: productAttrsSchema.optional(),
    status: productStatusSchema.optional(),
  })
  .refine((patch) => Object.values(patch).some((v) => v !== undefined), "At least one field is required");
export type UpdateProductInput = z.infer<typeof updateProductSchema>;

export const listProductsQuerySchema = paginationSchema.extend({
  /** Case-insensitive substring match on sku or name. */
  search: z.string().trim().min(1).max(200).optional(),
});
export type ListProductsQuery = z.infer<typeof listProductsQuerySchema>;

export const productIdParamSchema = z.object({ productId: z.string().uuid() });

export const mediaIdParamSchema = z.object({
  productId: z.string().uuid(),
  mediaId: z.string().uuid(),
});

/**
 * EN 18239 (draft) legitimate-interest party roles served by the restricted
 * tier. The draft Turkish ESPR regulation (Art. 13(1)(b)) names the same
 * actors: repairers, recyclers, customs, refurbishers/remanufacturers,
 * distributors and the competent (market-surveillance) authority.
 */
export const ACCESS_GRANT_ROLES = [
  "repairer",
  "recycler",
  "customs",
  "partner",
  "authority",
  "refurbisher",
  "distributor",
] as const;
export const accessGrantRoleSchema = z.enum(ACCESS_GRANT_ROLES);

/** Restricted-tier audience: which grant roles may see an item (empty/absent = every role). */
export const audienceSchema = z.array(accessGrantRoleSchema).max(ACCESS_GRANT_ROLES.length).nullish();
/** Same, from a comma-separated query-string value ("recycler,customs"). */
export const audienceQueryField = z
  .string()
  .trim()
  .optional()
  // `unknown`: the split values are validated by the piped audienceSchema, not trusted here.
  .transform((s): unknown =>
    s
      ? s
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
      : undefined,
  )
  .pipe(audienceSchema);

/* --- Product documents (draft TR ESPR Art. 27(7)–(9), EK-3 d/e) ----------------- */

/**
 * What a passport can carry as an attached document. Instructions and safety
 * information must be IN the passport, downloadable and online for the
 * product's life (Art. 27(7)–(9)); the declaration of conformity and
 * certificates are EK-3 (d). A technical file is confidential — only a
 * reference is recorded, never the file.
 */
export const DOCUMENT_KINDS = [
  "instructions",
  "safety_information",
  "declaration_of_conformity",
  "test_report",
  "certificate",
  "authorised_rep_mandate",
  "technical_file_reference",
  "other",
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];
export const documentKindSchema = z.enum(DOCUMENT_KINDS);

/** Kinds whose bytes are consumer-facing by default; the rest stay restricted-tier. */
export function defaultDocumentTier(kind: DocumentKind): "public" | "restricted" {
  return kind === "instructions" ||
    kind === "safety_information" ||
    kind === "declaration_of_conformity" ||
    kind === "certificate"
    ? "public"
    : "restricted";
}

const langCodeField = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z]{2}$/, "ISO 639-1 language code expected");

/** Query string of the raw-bytes upload (the body is the PDF itself). */
export const uploadDocumentQuerySchema = z.object({
  kind: documentKindSchema,
  title: z.string().trim().min(1).max(200),
  lang: langCodeField.default("tr"),
  tier: z.enum(["public", "restricted"]).optional(),
  audience: audienceQueryField,
  /** The document this one revises (kept, but no longer listed as current). */
  supersedesId: z.string().uuid().optional(),
});
export type UploadDocumentQuery = z.infer<typeof uploadDocumentQuerySchema>;

/** A byte-less reference document (technical file location, external certificate). */
export const createDocumentReferenceSchema = z.object({
  kind: documentKindSchema,
  title: z.string().trim().min(1).max(200),
  lang: langCodeField.default("tr"),
  tier: z.enum(["public", "restricted"]).optional(),
  audience: audienceSchema,
  referenceNote: z.string().trim().min(1).max(1000),
  referenceUrl: httpUrl(500).optional(),
});
export type CreateDocumentReferenceInput = z.infer<typeof createDocumentReferenceSchema>;

export const documentIdParamSchema = z.object({
  productId: z.string().uuid(),
  documentId: z.string().uuid(),
});

export const withdrawDocumentSchema = z.object({
  reason: z.string().trim().min(1).max(500).optional(),
});

export const identifierSchemeSchema = z.enum(["gs1", "internal"]);
export const identifierGranularitySchema = z.enum(["model", "batch", "item"]);

/**
 * GS1 AI (10) batch/lot: 1–20 characters from the GS1 "82" character set
 * (alphanumerics plus a fixed punctuation subset). Anything else would not be
 * encodable on a GS1 data carrier, so it is rejected up front.
 */
export const gs1LotSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9!"%&'()*+,\-./:;<=>?_]{1,20}$/, "Lot must be 1-20 GS1-encodable characters");

export const createIdentifierSchema = z.object({
  scheme: identifierSchemeSchema,
  granularity: identifierGranularitySchema,
  /** Required for batch-granularity GS1 identifiers (…/10/<lot>); optional on item level. */
  lot: gs1LotSchema.optional(),
  /** Required for item-granularity GS1 identifiers (…/21/<serial>). */
  serial: z.string().trim().min(1).max(128).optional(),
});
export type CreateIdentifierInput = z.infer<typeof createIdentifierSchema>;

export const identifierIdParamSchema = z.object({
  productId: z.string().uuid(),
  identifierId: z.string().uuid(),
});

/**
 * Withdrawing an identifier (draft Turkish ESPR regulation Art. 14(5), EN 18219
 * lifecycle): the data carrier keeps resolving — printed labels never die —
 * but the identifier is flagged and can no longer anchor a new passport.
 */
export const withdrawIdentifierSchema = z.object({
  reason: z.string().trim().min(1).max(500).optional(),
});
export type WithdrawIdentifierInput = z.infer<typeof withdrawIdentifierSchema>;

/* --- Passports (DPP domain) -------------------------------------------------- */

export const passportStatusSchema = z.enum(["draft", "published", "archived"]);
export type PassportStatus = z.infer<typeof passportStatusSchema>;

export const createPassportSchema = z.object({
  productId: z.string().uuid(),
  /** Optional link to a specific identifier (batch/item passports); must belong to the product. */
  identifierId: z.string().uuid().optional(),
  /** Lineage (Art. 13(1)(ç)) — the passport this one descends from; see updateLineageSchema. */
  originalPassportId: z.string().uuid().optional(),
  originalPassportUri: httpUrl(500).optional(),
  lineageReason: z.enum(["refurbished", "remanufactured", "repurposed", "reissued"]).optional(),
});
export type CreatePassportInput = z.infer<typeof createPassportSchema>;

export const listPassportsQuerySchema = paginationSchema.extend({
  productId: z.string().uuid().optional(),
});
export type ListPassportsQuery = z.infer<typeof listPassportsQuerySchema>;

export const passportIdParamSchema = z.object({ passportId: z.string().uuid() });

/**
 * Lineage (draft TR ESPR Art. 13(1)(ç)): why a new passport exists for a
 * product that already had one, and which passport it descends from. Exactly
 * one of id (same org) / URI (any operator) may be set; both null clears.
 */
export const LINEAGE_REASONS = ["refurbished", "remanufactured", "repurposed", "reissued"] as const;
export type LineageReason = (typeof LINEAGE_REASONS)[number];
export const lineageReasonSchema = z.enum(LINEAGE_REASONS);
export const updateLineageSchema = z
  .object({
    originalPassportId: z.string().uuid().nullable().optional(),
    originalPassportUri: httpUrl(500).nullable().optional(),
    lineageReason: lineageReasonSchema.nullable().optional(),
  })
  .refine((v) => !(v.originalPassportId && v.originalPassportUri), {
    message: "Name the original by id OR by URI, not both",
  });
export type UpdateLineageInput = z.infer<typeof updateLineageSchema>;

/** Official full-tier dump of one version (draft TR ESPR Art. 27(15): Turkish, within 15 days). */
export const exportVersionQuerySchema = z.object({
  lang: z.enum(["tr", "en", "de"]).default("tr"),
  format: z.enum(["html", "jsonld"]).default("html"),
});

/**
 * Market dates that drive the retention clock (draft TR ESPR Art. 11(3)(ğ),
 * 27(9)). `placedOnMarketAt` is set automatically at first publish and may be
 * corrected; `lastUnitPlacedOnMarketAt` is optional (ERP or manual) and
 * extends the clock for long-running models. null clears a value.
 */
const marketDateField = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected ISO date (YYYY-MM-DD)");
export const updateMarketDatesSchema = z
  .object({
    placedOnMarketAt: marketDateField.nullable().optional(),
    lastUnitPlacedOnMarketAt: marketDateField.nullable().optional(),
  })
  .refine((v) => v.placedOnMarketAt !== undefined || v.lastUnitPlacedOnMarketAt !== undefined, {
    message: "At least one date is required",
  });
export type UpdateMarketDatesInput = z.infer<typeof updateMarketDatesSchema>;

/**
 * Organization lifecycle. `wound_down` is the insolvency/liquidation case of
 * draft TR ESPR Art. 13(1)(d): the panel closes for writes, the public
 * passport surface and the backup pipeline keep running.
 */
export const ORG_STATUSES = ["active", "suspended", "wound_down"] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];
export const setOrgStatusSchema = z.object({
  status: z.enum(ORG_STATUSES),
  reason: z.string().trim().min(1).max(500).optional(),
});
export type SetOrgStatusInput = z.infer<typeof setOrgStatusSchema>;

export const passportVersionParamSchema = z.object({
  passportId: z.string().uuid(),
  versionNo: z.coerce.number().int().min(1),
});

/**
 * Product CSV import body. The CSV is carried as a JSON string field (not a
 * raw text/csv body) so the standard JSON pipeline (parsing, body limit,
 * validation) applies. Expected header: sku,name,gtin,schemaId plus any number
 * of attribute columns addressed by field dot-path id (e.g. "identity.brand").
 */
export const importProductsSchema = z.object({ csv: z.string().min(1).max(1_000_000) });
export type ImportProductsInput = z.infer<typeof importProductsSchema>;

/* --- Supplier data collection (DPP domain) ------------------------------------ */

/**
 * Hard ceiling on supplier-invite lifetime. A fixed product rule rather than an
 * env var: a share link is an unauthenticated capability, and no deployment
 * should quietly extend its exposure window through configuration.
 */
export const SUPPLIER_INVITE_MAX_TTL_DAYS = 90;

/** "expired" is derived from expiresAt for pending invites (also persisted lazily). */
export const supplierInviteStatusSchema = z.enum(["pending", "submitted", "applied", "revoked", "expired"]);
export type SupplierInviteStatus = z.infer<typeof supplierInviteStatusSchema>;

export const createSupplierInviteSchema = z.object({
  supplierName: z.string().trim().min(1).max(120),
  supplierEmail: emailSchema.optional(),
  /** Field dot-path ids to request — validated against the product's schema by the service. */
  fieldIds: z
    .array(z.string().trim().min(1).max(128))
    .min(1)
    .max(200)
    .transform((ids) => [...new Set(ids)]),
  expiresInDays: z.coerce.number().int().min(1).max(SUPPLIER_INVITE_MAX_TTL_DAYS).default(30),
  note: z.string().trim().min(1).max(500).optional(),
});
export type CreateSupplierInviteRequest = z.input<typeof createSupplierInviteSchema>;
export type CreateSupplierInviteInput = z.infer<typeof createSupplierInviteSchema>;

export const listSupplierInvitesQuerySchema = paginationSchema.extend({
  productId: z.string().uuid().optional(),
  status: supplierInviteStatusSchema.optional(),
});
export type ListSupplierInvitesQuery = z.infer<typeof listSupplierInvitesQuerySchema>;

export const inviteIdParamSchema = z.object({ inviteId: z.string().uuid() });

/** Apply body — omitted fieldIds means "apply every submitted field". */
export const applySupplierSubmissionSchema = z.object({
  fieldIds: z
    .array(z.string().trim().min(1).max(128))
    .min(1)
    .max(200)
    .transform((ids) => [...new Set(ids)])
    .optional(),
});
export type ApplySupplierSubmissionInput = z.infer<typeof applySupplierSubmissionSchema>;

/* --- Restricted-tier access grants (DPP domain, EN 18239) ----------------------- */

/**
 * Hard ceiling on access-grant lifetime. Longer than supplier invites (a
 * repairer/customs relationship outlives a data-collection round) but still a
 * fixed product rule, not an env var — an unauthenticated capability's exposure
 * window must never be quietly extended through configuration.
 */
export const ACCESS_GRANT_MAX_TTL_DAYS = 365;

export type AccessGrantRole = z.infer<typeof accessGrantRoleSchema>;

/** "expired" is derived from expiresAt for active grants — never persisted. */
export const accessGrantStatusSchema = z.enum(["active", "revoked", "expired"]);
export type AccessGrantStatus = z.infer<typeof accessGrantStatusSchema>;

export const createAccessGrantSchema = z.object({
  role: accessGrantRoleSchema,
  /** Who the grant was issued to (organization or person) — panel bookkeeping. */
  holderName: z.string().trim().min(1).max(120),
  expiresInDays: z.coerce.number().int().min(1).max(ACCESS_GRANT_MAX_TTL_DAYS).default(90),
});
export type CreateAccessGrantRequest = z.input<typeof createAccessGrantSchema>;
export type CreateAccessGrantInput = z.infer<typeof createAccessGrantSchema>;

export const listAccessGrantsQuerySchema = paginationSchema.extend({
  status: accessGrantStatusSchema.optional(),
  role: accessGrantRoleSchema.optional(),
});
export type ListAccessGrantsQuery = z.infer<typeof listAccessGrantsQuerySchema>;

export const grantIdParamSchema = z.object({ grantId: z.string().uuid() });

/* --- Machine integration: API keys & webhooks (DPP domain) ---------------------- */

/**
 * Machine-principal scopes. An API key's scopes are its SOLE authority — user
 * RBAC/MFA never applies to a machine caller — so the catalog is deliberately
 * narrow: exactly the panel data surface an ERP connector needs, nothing else.
 */
export const API_KEY_SCOPES = [
  "products:read",
  "products:write",
  "passports:read",
  "passports:publish",
] as const;
export const apiKeyScopeSchema = z.enum(API_KEY_SCOPES);
export type ApiKeyScope = z.infer<typeof apiKeyScopeSchema>;

/** Scope check for machine principals (the only authorization an API key gets). */
export function hasApiKeyScope(scopes: readonly string[], required: ApiKeyScope): boolean {
  return scopes.includes(required);
}

/** Hard ceiling on API-key lifetime when an expiry is set (10 years). */
export const API_KEY_MAX_TTL_DAYS = 3650;

export const createApiKeySchema = z.object({
  /** Operator label, e.g. "Logo connector". */
  name: z.string().trim().min(1).max(120),
  scopes: z
    .array(apiKeyScopeSchema)
    .min(1)
    .transform((s) => [...new Set(s)]),
  /** Omitted = the key does not expire (long-running ERP integrations). */
  expiresInDays: z.coerce.number().int().min(1).max(API_KEY_MAX_TTL_DAYS).optional(),
});
export type CreateApiKeyRequest = z.input<typeof createApiKeySchema>;
export type CreateApiKeyInput = z.infer<typeof createApiKeySchema>;

/** "expired" is derived from expiresAt at read/auth time — never persisted. */
export const apiKeyStatusSchema = z.enum(["active", "revoked", "expired"]);
export type ApiKeyStatus = z.infer<typeof apiKeyStatusSchema>;

export const apiKeyIdParamSchema = z.object({ keyId: z.string().uuid() });

/** Events an org webhook can subscribe to. */
export const WEBHOOK_EVENTS = ["passport.published", "passport.archived", "product.updated"] as const;
export const webhookEventSchema = z.enum(WEBHOOK_EVENTS);
export type WebhookEvent = z.infer<typeof webhookEventSchema>;

export const webhookStatusSchema = z.enum(["active", "disabled"]);
export type WebhookStatus = z.infer<typeof webhookStatusSchema>;

const webhookEventsField = z
  .array(webhookEventSchema)
  .min(1)
  .transform((e) => [...new Set(e)]);

/** URL shape only — the https-only-in-production rule lives in the service. */
export const createWebhookSchema = z.object({
  url: httpUrl(2000),
  events: webhookEventsField,
});
export type CreateWebhookRequest = z.input<typeof createWebhookSchema>;
export type CreateWebhookInput = z.infer<typeof createWebhookSchema>;

export const updateWebhookSchema = z
  .object({
    url: httpUrl(2000).optional(),
    events: webhookEventsField.optional(),
    status: webhookStatusSchema.optional(),
  })
  .refine((patch) => Object.values(patch).some((v) => v !== undefined), "At least one field is required");
export type UpdateWebhookRequest = z.input<typeof updateWebhookSchema>;
export type UpdateWebhookInput = z.infer<typeof updateWebhookSchema>;

export const webhookIdParamSchema = z.object({ webhookId: z.string().uuid() });
export const webhookDeliveryParamSchema = z.object({
  webhookId: z.string().uuid(),
  deliveryId: z.string().uuid(),
});

export const webhookDeliveryStatusSchema = z.enum(["pending", "delivered", "failed"]);
export type WebhookDeliveryStatus = z.infer<typeof webhookDeliveryStatusSchema>;

/* --- Supply chain master data: facilities, certifications, stages, components */

const isoDateField = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected ISO date (YYYY-MM-DD)");
const tierField = z.enum(["public", "restricted"]);

export const facilityBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  countryIso: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, "ISO 3166-1 alpha-2 code expected"),
  city: z.string().trim().max(120).optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  employeeCount: z.number().int().min(0).max(1_000_000).optional(),
  renewableEnergyPct: z.number().int().min(0).max(100).optional(),
  waterRecycling: z.boolean().optional(),
  wastewaterTreatment: z.boolean().optional(),
  practicesNote: z.string().trim().max(2000).optional(),
  /**
   * Unique facility identifier (draft TR ESPR Art. 14, EK-3 ğ): a GS1 GLN
   * (13 digits, mod-10 checked by the service) or an org-local code. Absent
   * means the platform derives a stable URN from the row id at publish time.
   */
  identifierScheme: z.enum(["GLN", "internal"]).optional(),
  identifierValue: z.string().trim().min(1).max(64).optional(),
});
export type FacilityBodyInput = z.infer<typeof facilityBodySchema>;
export const facilityIdParamSchema = z.object({ facilityId: z.string().uuid() });

export const certificationBodySchema = z.object({
  scheme: z.string().trim().min(1).max(120),
  certificateNo: z.string().trim().max(120).optional(),
  issuer: z.string().trim().max(200).optional(),
  validFrom: isoDateField.optional(),
  validUntil: isoDateField.optional(),
  evidenceUrl: httpUrl(2000).optional(),
});
export type CertificationBodyInput = z.infer<typeof certificationBodySchema>;
export const certificationIdParamSchema = z.object({ certificationId: z.string().uuid() });

/** Idempotent PUT of an attachment set (facility/product certifications). */
export const certificationLinksSchema = z.object({
  certificationIds: z.array(z.string().uuid()).max(50),
});

export const supplyChainStageKindSchema = z.enum([
  "design",
  "raw_material",
  "spinning",
  "weaving",
  "dyeing",
  "sampling",
  "trims",
  "manufacturing",
  "delivery",
]);
export const supplyChainPutSchema = z.object({
  stages: z
    .array(
      z.object({
        stage: supplyChainStageKindSchema,
        facilityId: z.string().uuid().nullish(),
        transportMethod: z.string().trim().max(120).nullish(),
        notes: z.string().trim().max(2000).nullish(),
        tier: tierField,
        audience: audienceSchema,
      }),
    )
    .max(30),
});
export type SupplyChainPutInput = z.infer<typeof supplyChainPutSchema>;

export interface ComponentNodeInput {
  name: string;
  componentType?: string | null | undefined;
  facilityId?: string | null | undefined;
  imageMediaId?: string | null | undefined;
  weightG?: number | null | undefined;
  materialNote?: string | null | undefined;
  isSeparable?: boolean | null | undefined;
  tier: "public" | "restricted";
  audience?: AccessGrantRole[] | null | undefined;
  children?: ComponentNodeInput[] | undefined;
}
const componentNodeSchema: z.ZodType<ComponentNodeInput> = z.lazy(() =>
  z.object({
    name: z.string().trim().min(1).max(200),
    componentType: z.string().trim().max(120).nullish(),
    facilityId: z.string().uuid().nullish(),
    imageMediaId: z.string().uuid().nullish(),
    weightG: z.number().int().min(0).max(1_000_000).nullish(),
    materialNote: z.string().trim().max(1000).nullish(),
    isSeparable: z.boolean().nullish(),
    tier: tierField,
    audience: audienceSchema,
    children: z.array(componentNodeSchema).max(50).optional(),
  }),
);
export const componentsPutSchema = z.object({ components: z.array(componentNodeSchema).max(100) });
export type ComponentsPutInput = z.infer<typeof componentsPutSchema>;

/* --- LCA & circularity partners (Faz D) -------------------------------------- */

export const lcaSourceSchema = z.enum(["manual", "epd", "clca"]);

export const lcaPutSchema = z.object({
  source: lcaSourceSchema,
  externalRef: z.string().trim().max(200).nullish(),
  method: z.string().trim().max(120).nullish(),
  functionalUnit: z.string().trim().max(120).nullish(),
  systemBoundary: z.string().trim().max(120).nullish(),
  dataset: z.string().trim().max(120).nullish(),
  calculatedAt: isoDateField.nullish(),
  verifiedBy: z.string().trim().max(200).nullish(),
  indicators: z
    .array(
      z.object({
        indicator: z.string().trim().min(1).max(60),
        value: z.number().finite(),
        unit: z.string().trim().min(1).max(30),
        module: z.string().trim().max(20).optional(),
      }),
    )
    .max(40),
});
export type LcaPutInput = z.infer<typeof lcaPutSchema>;

/** The five canonical categories — localized labels everywhere they render. */
export const partnerCategorySchema = z.enum(["donation", "repair", "upcycle", "recycle", "resale"]);
export type PartnerCategory = z.infer<typeof partnerCategorySchema>;
export const PARTNER_CATEGORIES = partnerCategorySchema.options;

/**
 * Stored category: a canonical key OR an org-defined label typed by the user.
 * Free text on purpose — brands run circularity programs the five keys do not
 * cover ("takas", "kiralama", …); unknown values render verbatim.
 */
export const partnerCategoryValueSchema = z.string().trim().min(1).max(40);

export const partnerBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  category: partnerCategoryValueSchema,
  countryIso: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, "ISO 3166-1 alpha-2 code expected")
    .optional(),
  city: z.string().trim().max(120).optional(),
  url: httpUrl(2000).optional(),
});
export type PartnerBodyInput = z.infer<typeof partnerBodySchema>;
export const partnerIdParamSchema = z.object({ partnerId: z.string().uuid() });

export const partnerLinksSchema = z.object({ partnerIds: z.array(z.string().uuid()).max(100) });

/* --- Public page theme (white-label) ------------------------------------------ */

export const orgThemeSchema = z.object({
  accentColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb")
    .optional(),
  disabledSections: z.array(z.string().trim().min(1).max(40)).max(30).optional(),
  sectionLabels: z.record(z.string().min(1).max(40), z.string().trim().min(1).max(80)).optional(),
});
export type OrgThemeInput = z.infer<typeof orgThemeSchema>;

/* --- Economic operators (draft TR ESPR Art. 27–29, EK-3 f/g/h/ı) ------------- */

/**
 * The parties a passport must name. Manufacturer and importer contact details
 * belong on the PUBLIC part of the passport (Art. 27(6), 29(5)); the
 * authorised representative holds the manufacturer's written mandate
 * (Art. 28); the responsible person is the operator established in the
 * target market for product-safety duties (EK-3 ı); the DPP service provider
 * hosts the backup copy (EK-3 i).
 */
export const OPERATOR_ROLES = [
  "manufacturer",
  "importer",
  "authorised_representative",
  "responsible_person",
  "dpp_service_provider",
  "distributor",
] as const;
export type OperatorRole = (typeof OPERATOR_ROLES)[number];
export const operatorRoleSchema = z.enum(OPERATOR_ROLES);

/** Unique operator identifier schemes (Art. 14; GS1 GLN preferred, ISO/IEC 15459 family). */
export const OPERATOR_IDENTIFIER_SCHEMES = [
  "GLN",
  "EORI",
  "VAT",
  "LEI",
  "NTR",
  "MERSIS",
  "VKN",
  "DUNS",
  "LOCAL",
] as const;
export type OperatorIdentifierScheme = (typeof OPERATOR_IDENTIFIER_SCHEMES)[number];
export const operatorIdentifierSchemeSchema = z.enum(OPERATOR_IDENTIFIER_SCHEMES);

/** Syntactic shape per scheme; GLN check digits are verified by the service (mod-10). */
const OPERATOR_IDENTIFIER_PATTERNS: Record<OperatorIdentifierScheme, RegExp> = {
  GLN: /^\d{13}$/,
  EORI: /^[A-Z]{2}[A-Z0-9]{1,15}$/,
  VAT: /^[A-Z]{2}[A-Z0-9]{2,13}$/,
  LEI: /^[A-Z0-9]{20}$/,
  NTR: /^[A-Za-z0-9 ./-]{1,50}$/,
  MERSIS: /^\d{16}$/,
  VKN: /^\d{10}$/,
  DUNS: /^\d{9}$/,
  LOCAL: /^[A-Za-z0-9 ._:/-]{1,50}$/,
};

export function operatorIdentifierMatchesScheme(scheme: OperatorIdentifierScheme, value: string): boolean {
  return OPERATOR_IDENTIFIER_PATTERNS[scheme].test(value);
}

/** EORI: ISO country code + up to 15 alphanumerics (EK-3 h — the importer's customs identity). */
export const eoriSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}[A-Z0-9]{1,15}$/, "EORI must be a country code followed by up to 15 alphanumerics");

export const operatorBodySchema = z
  .object({
    role: operatorRoleSchema,
    /** The org-wide default for its role (one per role); products may override. */
    isDefault: z.boolean().optional(),
    legalName: z.string().trim().min(1).max(200),
    tradeName: z.string().trim().max(200).optional(),
    streetAddress: z.string().trim().max(200).optional(),
    extendedAddress: z.string().trim().max(200).optional(),
    locality: z.string().trim().max(120).optional(),
    postalCode: z.string().trim().max(20).optional(),
    region: z.string().trim().max(120).optional(),
    countryIso: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/, "ISO 3166-1 alpha-2 code expected"),
    email: z.string().trim().email().max(254).optional(),
    phone: z.string().trim().max(40).optional(),
    website: httpUrl(500).optional(),
    identifierScheme: operatorIdentifierSchemeSchema.optional(),
    identifierValue: z.string().trim().toUpperCase().min(1).max(50).optional(),
    eori: eoriSchema.optional(),
    /**
     * Whether the contact block is public. Manufacturer and importer default to
     * public — the regulation puts their address on the public part; other
     * roles may stay restricted-tier.
     */
    contactTier: tierField.optional(),
    audience: audienceSchema,
    mandateValidFrom: isoDateField.optional(),
    mandateValidUntil: isoDateField.optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.identifierScheme === undefined) !== (v.identifierValue === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["identifierValue"],
        message: "identifierScheme and identifierValue must be given together",
      });
    } else if (
      v.identifierScheme !== undefined &&
      v.identifierValue !== undefined &&
      !operatorIdentifierMatchesScheme(v.identifierScheme, v.identifierValue)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["identifierValue"],
        message: `Identifier does not match the ${v.identifierScheme} format`,
      });
    }
    if (v.mandateValidFrom && v.mandateValidUntil && v.mandateValidUntil < v.mandateValidFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mandateValidUntil"],
        message: "Mandate end must not precede its start",
      });
    }
  });
/** The request body as the client sends it (before defaults/transforms). */
export type OperatorBodyRequest = z.input<typeof operatorBodySchema>;
export type OperatorBodyInput = z.infer<typeof operatorBodySchema>;
export const operatorIdParamSchema = z.object({ operatorId: z.string().uuid() });

/** Idempotent PUT of a product's per-role operator overrides (null = fall back to the org default). */
export const productOperatorsBodySchema = z.object({
  assignments: z
    .array(z.object({ role: operatorRoleSchema, operatorId: z.string().uuid().nullable() }))
    .max(OPERATOR_ROLES.length),
});
export type ProductOperatorsInput = z.infer<typeof productOperatorsBodySchema>;

/** Contact tier defaults per role — public where the regulation demands it. */
export function defaultContactTier(role: OperatorRole): "public" | "restricted" {
  return role === "manufacturer" || role === "importer" || role === "responsible_person"
    ? "public"
    : "restricted";
}
